import React from "react";
import KuikaSvg, { KuikaSvgProps } from "../../../../ui-kit/kuika-svg/kuika-svg";
import { ShowcaseFrame } from "../_layout/showcase-frame";
import { SpecRow } from "../_layout/spec-row";
import { VariantGrid } from "../_layout/variant-grid";

type SvgType = NonNullable<KuikaSvgProps["type"]>;

interface ISvgSample {
  type: SvgType;
  name: string;
}

const COMMON_SAMPLES: ISvgSample[] = [
  { type: "common", name: "check" },
  { type: "common", name: "x" },
  { type: "common", name: "search" },
  { type: "common", name: "trash" },
  { type: "common", name: "star" },
  { type: "common", name: "settings" }
];

const NAMESPACE_SAMPLES: ISvgSample[] = [
  { type: "common", name: "info-circle" },
  { type: "arrows", name: "chevron-right" },
  { type: "topbar-icons", name: "save" },
  { type: "sidebar-icons", name: "datasource" },
  { type: "styling-panel-icons", name: "border" },
  { type: "datasource-icons", name: "postgresql" },
  { type: "action-icons", name: "send" },
  { type: "flags", name: "tr" },
  { type: "workflow-icons", name: "trigger" },
  { type: "screen-templates", name: "login" },
  { type: "agent-icons", name: "brain" }
];

const SIZES: number[] = [16, 24, 32, 48];
const COLORS: string[] = ["#0f172a", "#1d4ed8", "#15803d", "#b91c1c"];

export const SvgStory: React.FC = (): JSX.Element => {
  return (
    <ShowcaseFrame
      title="SVG"
      componentName="KuikaSvg"
      sourcePath="ui-kit/kuika-svg"
      description="Inline SVG renderer across thirteen icon namespaces with configurable size, color override and pointer affordance. Unresolved names render an empty box."
    >
      <SpecRow label="Common namespace" caption="type='common' — most-used icons">
        {COMMON_SAMPLES.map((sample: ISvgSample) => (
          <div key={sample.name} className="lab-stack" style={{ alignItems: "center", gap: 4 }}>
            <KuikaSvg type={sample.type} name={sample.name} width={20} height={20} />
            <span className="lab-caption">{sample.name}</span>
          </div>
        ))}
      </SpecRow>
      <SpecRow label="Namespaces" caption="one icon from each category">
        {NAMESPACE_SAMPLES.map((sample: ISvgSample) => (
          <div key={`${sample.type}-${sample.name}`} className="lab-stack" style={{ alignItems: "center", gap: 4 }}>
            <KuikaSvg type={sample.type} name={sample.name} width={20} height={20} />
            <span className="lab-caption">{sample.type}</span>
          </div>
        ))}
      </SpecRow>
      <VariantGrid
        label="size"
        cols={SIZES.map((s: number) => ({ value: String(s), label: `${s}px` }))}
        cellMinWidth={120}
        render={(size: string): React.ReactNode => (
          <KuikaSvg name="star" width={Number(size)} height={Number(size)} color="#0f172a" />
        )}
      />
      <SpecRow label="Color override" caption="color prop">
        {COLORS.map((color: string) => (
          <KuikaSvg key={color} name="star" width={24} height={24} color={color} />
        ))}
      </SpecRow>
    </ShowcaseFrame>
  );
};
